import { Button, Container, Link, Media, Socials } from '@portfolio/components'
import { LogoWhite } from '@portfolio/icons'
import {
  NavigationItem as NavigationItemType,
  NavigationNestedItem,
  SiteConfiguration,
} from '@portfolio/models'
import { styled } from '@portfolio/styles'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronBig } from '@portfolio/icons'
import { MobileNestedNavigationItem } from './components/MobileNestedNavigationItem'

const Wrap = styled('div', {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  background: '$black',
  color: '$white',
  gap: '$24',
  overflowY: 'hidden',
  touchAction: 'none',
})

const DrawerHeader = styled(Container, {
  minHeight: '$headerHeight',
  projectFont: 'body03',
  top: 0,
  left: 0,
  alignItems: 'center',
  display: 'grid',
  justifyContent: 'space-between',
  gap: '$24',
  gridAutoFlow: 'column',
})

const LogoWrapper = styled(Link, {
  width: '100%',
  borderRadius: '$r1',
  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
    outlineOffset: '8px',
  },
})

const CloseButton = styled(Button, {
  projectFont: 'body03',
})

const ListWrap = styled('ul', {
  display: 'flex',
  flexDirection: 'column',
  justifyItems: 'center',
  gap: '$40',
  padding: '0 $20',
})

const NavWrap = styled('nav', {
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'end',

  variants: {
    isOverlayVisible: {
      true: {
        transform: 'translateX(-100%)',
        visibility: 'hidden',
      },
    },
  },
})

const NavigationItemWrapper = styled('li', {
  padding: 0,
  display: 'grid',
  variants: {
    withIcon: {
      true: {
        marginLeft: '-$24',
      },
    },
  },
})

const NavLinkContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',

  '& a, & button': {
    projectFont: 'heading07',
  },
})

const NestedMenuButton = styled(Button, {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const NavigationItemIcon = styled('span', {
  width: '$24',
  height: '$24',
  display: 'grid',
  alignSelf: 'center',
})

const NavigationItemLink = styled(Link, {
  variants: {
    highlighted: {
      true: {
        '&::before': {
          content: '',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '$successGreen',
          alignSelf: 'center',
          marginRight: 10,
          marginBottom: 2,
        },
      },
    },
  },
})

const SocialsWrap = styled('div', {
  padding: '$20',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease',

  variants: {
    isOverlayVisible: {
      true: {
        transform: 'translateX(-100%)',
        visibility: 'hidden',
      },
    },
  },
})

type ContentProps = {
  onCloseClick?: (event: React.MouseEvent | React.KeyboardEvent) => void
  header: SiteConfiguration['header']
  theme: 'transparent-dark' | 'transparent-light'
}

export const MobileDrawer = ({ header, onCloseClick }: ContentProps) => {
  const { t } = useTranslation('header')
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [selectedNavItem, setSelectedNavItem] = useState<
    NavigationNestedItem | undefined | null
  >(null)

  const onNestedMenuClick = (navItem: NavigationNestedItem) => {
    setIsOverlayVisible(true)
    setSelectedNavItem(navItem)
  }

  const onBack = () => {
    setIsOverlayVisible(false)
    setSelectedNavItem(null)
  }

  return (
    <Wrap>
      <DrawerHeader>
        <LogoWrapper appearance="grid" href="/">
          <LogoWhite />
        </LogoWrapper>
        <CloseButton appearance="text" onClick={onCloseClick}>
          {t('close')}
        </CloseButton>
      </DrawerHeader>
      <NavWrap isOverlayVisible={isOverlayVisible}>
        <ListWrap>
          {header?.navLinks &&
            header?.navLinks?.length > 0 &&
            header?.navLinks?.map((navItem) => (
              <NavigationItem
                key={navItem._key}
                navItem={navItem}
                onNestedMenuClick={onNestedMenuClick}
              />
            ))}
        </ListWrap>
      </NavWrap>
      <SocialsWrap isOverlayVisible={isOverlayVisible}>
        <Socials items={header?.socials?.items} />
      </SocialsWrap>
      <MobileNestedNavigationItem
        onBack={onBack}
        isVisible={isOverlayVisible}
        selectedNavItem={selectedNavItem}
      />
    </Wrap>
  )
}

const NavigationItem = ({
  navItem,
  onNestedMenuClick,
}: {
  navItem: NavigationItemType
  onNestedMenuClick: (navItem: NavigationNestedItem) => void
}) => {
  if (
    navItem._type === 'nestedNavigation' &&
    navItem.ctaLabel &&
    navItem?.ctaLink &&
    navItem.linkGroups?.length
  ) {
    return (
      <NavigationItemWrapper withIcon={!!navItem.ctaIcon}>
        <NavLinkContainer>
          <NestedMenuButton onClick={() => onNestedMenuClick(navItem)}>
            {navItem.ctaIcon?.mediaPayload && (
              <NavigationItemIcon>
                <Media {...navItem.ctaIcon} priority={false} sizes={'40px'} />
              </NavigationItemIcon>
            )}
            {navItem.ctaLabel}
            <ChevronBig />
          </NestedMenuButton>
        </NavLinkContainer>
      </NavigationItemWrapper>
    )
  }

  return (
    <NavigationItemWrapper withIcon={!!navItem.ctaIcon}>
      <NavLinkContainer>
        {navItem.ctaLink && (
          <NavigationItemLink
            appearance="grid"
            highlighted={
              navItem._type === 'navigationLink' && navItem.highlighted
            }
            {...navItem.ctaLink}
          >
            {navItem.ctaIcon?.mediaPayload && (
              <NavigationItemIcon>
                <Media {...navItem.ctaIcon} priority={false} sizes={'40px'} />
              </NavigationItemIcon>
            )}
            {navItem.ctaLabel}
          </NavigationItemLink>
        )}
      </NavLinkContainer>
    </NavigationItemWrapper>
  )
}
